import { createContext, useContext, useRef } from "react";

import {
  type TicketMessageCitation,
  dedupeGraphCitations,
  dedupeRuleCitations,
  isGraphCitation,
  isKnowledgeRuleCitation,
  ruleCitationKey,
} from "./commentSourceUtils";

import { CLAIM_NODE } from "../../../utils/graphNodes";

/** The one message whose citations the Cited claims panel is showing. */
export type FocusedCitedClaims = {
  messageId: string;
  citations: TicketMessageCitation[];
};

/** A message as far as this selection cares: role, whether it has landed, and
 * what it cited. Structural on purpose so the route's fuller message type
 * satisfies it without this module importing the route. */
type CitableMessage = {
  _id: string;
  role?: string;
  content?: unknown;
  status?: string;
  citations?: readonly TicketMessageCitation[];
};

/** Citations from one message, in the order the panel renders them: rule chips
 * first, then the graph relationships behind them.
 *
 * why: the two are different shapes and `dedupeRuleCitations` filters graph
 * citations out. Deriving the panel's input from rules alone silently drops
 * every graph relationship the answer was built on.
 */
export function citationsForPanel(
  message: CitableMessage,
): TicketMessageCitation[] {
  const raw = message.citations ?? [];
  return [...dedupeRuleCitations(raw), ...dedupeGraphCitations(raw)];
}

/** Which message the panel should show.
 *
 * A clicked chip pins its own message (`focusedMessageId`); otherwise the most
 * recent assistant message that actually cited something wins, so the panel
 * tracks the conversation instead of emptying on a turn with no citations.
 * A pending message with no content yet is skipped - its citations arrive with
 * the content, and showing it early empties the panel mid-stream.
 */
function isSettledAnswer(message: CitableMessage): boolean {
  return (
    message.role === "assistant" &&
    !(!message.content && message.status === "pending")
  );
}

/** A registry row warhol filled the panel with, which the answer did not cite. */
export function isRelatedCitation(citation: TicketMessageCitation): boolean {
  return "related" in citation && citation.related === true;
}

/** `ruleCitationKey` -> the source's number in this thread. A source cited
 * again later keeps the number it was first given. Related rows are not
 * numbered: the chips count them as "+n related" rather than give them one. */
export function threadCitationNumbers(
  messages: readonly CitableMessage[],
): Map<string, number> {
  const cited = messages
    .filter(isSettledAnswer)
    .flatMap((message) => message.citations ?? [])
    .filter((citation) => !isRelatedCitation(citation));
  return new Map(
    dedupeRuleCitations(cited).map((citation, index) => [
      ruleCitationKey(citation),
      index + 1,
    ]),
  );
}

/** The thread's numbering, for the messages inside it. `null` outside a thread
 * that provides one: a message then numbers its own citations from 1. */
export const ThreadCitationNumbersContext = createContext<ReadonlyMap<
  string,
  number
> | null>(null);

export function useThreadCitationNumbersContext(): ReadonlyMap<
  string,
  number
> | null {
  return useContext(ThreadCitationNumbersContext);
}

type CitationLists = readonly (readonly TicketMessageCitation[] | undefined)[];

function sameLists(a: CitationLists, b: CitationLists): boolean {
  return a.length === b.length && a.every((list, i) => list === b[i]);
}

function sameNumbers(
  a: ReadonlyMap<string, number>,
  b: ReadonlyMap<string, number>,
): boolean {
  return (
    a.size === b.size &&
    Array.from(a.entries()).every(([key, n]) => b.get(key) === n)
  );
}

/** `threadCitationNumbers`, kept referentially stable while the numbering is
 * unchanged. why: the messages array changes on every streamed token, and a new
 * Map each time would re-render every message in the thread through context.
 * A frame whose answers still hold the same citation arrays skips the scan. */
export function useThreadCitationNumbers(
  messages: readonly CitableMessage[],
): ReadonlyMap<string, number> {
  const cache = useRef<{
    lists: CitationLists;
    numbers: ReadonlyMap<string, number>;
  } | null>(null);
  const lists = messages
    .filter(isSettledAnswer)
    .map((message) => message.citations);
  const previous = cache.current;
  if (previous && sameLists(previous.lists, lists)) return previous.numbers;
  const next = threadCitationNumbers(messages);
  const numbers =
    previous && sameNumbers(previous.numbers, next) ? previous.numbers : next;
  cache.current = { lists, numbers };
  return numbers;
}

export function selectFocusedCitedClaims(
  messages: readonly CitableMessage[],
  focusedMessageId?: string | null,
): FocusedCitedClaims | null {
  const pick = (message: CitableMessage): FocusedCitedClaims | null => {
    if (!isSettledAnswer(message)) return null;
    const citations = citationsForPanel(message);
    if (citations.length === 0) return null;
    return { messageId: message._id, citations };
  };

  if (focusedMessageId) {
    const targeted = messages.find((m) => m._id === focusedMessageId);
    if (targeted) {
      const hit = pick(targeted);
      if (hit) return hit;
    }
  }
  for (let i = messages.length - 1; i >= 0; i--) {
    const hit = pick(messages[i]);
    if (hit) return hit;
  }
  return null;
}

/** The focused answer's citations without the rules it only matched.
 *
 * why: the legacy chat's panel is titled "Cited claims" and has no related
 * group, so a `related` rule there contradicts the chip row under the answer,
 * which counts it as "+n related".
 *
 * why the graph pass: dropping the CHIP is not enough, because a related rule
 * reaches the panel a second way. warhol draws a `claim:<ruleId>` graph record
 * for every rule a retrieved memory names and runs that over the whole ranked
 * list, related fill rows included (`memory_citations.py::memory_records`,
 * called with `ranked` in `knowledge_citations.py`); the graph tool traverses
 * them too. `CitedClaimsPanel` renders such a record in its "Relationships"
 * block labelled with `graphLabel` — which is the related rule's own
 * statement. So the rule whose chip was just hidden was still listed under
 * "Cited claims", exactly the attribution this filter exists to prevent, and
 * on a turn whose rules were ALL fill it was the only thing the panel showed.
 * Only the claim nodes of the rules dropped HERE go; a cited rule's own node,
 * a neighbouring claim this turn never chipped, and every non-claim node stay.
 */
export function withoutRelatedClaims(
  focused: FocusedCitedClaims | null,
): FocusedCitedClaims | null {
  if (!focused) return null;
  // Collected in a full first pass: `citationsForPanel` emits every rule chip
  // before any graph record, so the node is seen after the rule that hides it.
  const hiddenNodes = new Set<string>();
  for (const citation of focused.citations) {
    if (isRelatedCitation(citation) && isKnowledgeRuleCitation(citation)) {
      hiddenNodes.add(`${CLAIM_NODE}${citation.ruleId}`);
    }
  }
  const citations = focused.citations.filter((citation) => {
    if (isRelatedCitation(citation)) return false;
    return !(
      isGraphCitation(citation) && hiddenNodes.has(citation.graphNodeId)
    );
  });
  return citations.length > 0 ? { ...focused, citations } : null;
}
